// Cliente HTTP de la app. Todo pasa por aquí para que el manejo de la cookie
// antifalsificación y de los 401 esté en un solo lugar.

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

const HEADER_CSRF = "X-CSRF-TOKEN";
const COOKIE_CSRF = "fv.csrf";
const TIPO_PROBLEMA_CSRF = "urn:fichas:antiforgery";
const METODOS_SEGUROS = new Set(["GET", "HEAD", "OPTIONS"]);

export class ErrorApi extends Error {
  constructor(
    readonly status: number,
    mensaje: string,
    /** Errores por campo cuando el servidor responde un ValidationProblem. */
    readonly errores: Record<string, string[]> = {},
  ) {
    super(mensaje);
  }
}

function leerCookie(nombre: string): string | null {
  const encontrada = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${nombre}=`));
  return encontrada ? decodeURIComponent(encontrada.slice(nombre.length + 1)) : null;
}

/**
 * Pide un token al servidor, que lo deja en una cookie legible.
 * `forzar` ignora el que ya haya: los tokens van ligados a la identidad, así
 * que el que se pidió anónimo deja de servir apenas se inicia sesión.
 */
let tokenCsrfEnMemoria: string | null = null;

/**
 * Pide un token al servidor, que lo deja en una cookie legible y en el cuerpo JSON.
 * En llamadas Cross-Origin el script no puede leer cookies del dominio de la API,
 * por lo que se guarda además el token en memoria.
 * `forzar` ignora el que ya haya: los tokens van ligados a la identidad, así
 * que el que se pidió anónimo deja de servir apenas se inicia sesión.
 */
async function obtenerToken(forzar = false): Promise<string | null> {
  if (!forzar) {
    if (tokenCsrfEnMemoria) return tokenCsrfEnMemoria;
    const existente = leerCookie(COOKIE_CSRF);
    if (existente) {
      tokenCsrfEnMemoria = existente;
      return existente;
    }
  }

  try {
    const res = await fetch(`${API_BASE_URL}/api/antiforgery`, {
      credentials: "include",
      cache: "no-store",
    });
    if (res.ok) {
      const datos = await res.json();
      if (datos?.token) {
        tokenCsrfEnMemoria = datos.token;
        return datos.token;
      }
    }
  } catch {
    // Si la lectura JSON falla, intentamos respaldo de cookie local
  }

  const cookieRespaldo = leerCookie(COOKIE_CSRF);
  if (cookieRespaldo) tokenCsrfEnMemoria = cookieRespaldo;
  return tokenCsrfEnMemoria;
}

type CuerpoError = {
  type?: string;
  title?: string;
  mensaje?: string;
  errors?: Record<string, string[]>;
};

async function leerError(respuesta: Response): Promise<CuerpoError> {
  try {
    return await respuesta.json();
  } catch {
    return {};
  }
}

/** El servidor usa los nombres de C# (PascalCase); el front, camelCase. */
function normalizarErrores(errores?: Record<string, string[]>): Record<string, string[]> {
  if (!errores) return {};
  return Object.fromEntries(
    Object.entries(errores).map(([campo, mensajes]) => [
      campo.charAt(0).toLowerCase() + campo.slice(1),
      mensajes,
    ]),
  );
}

function primerError(errores: Record<string, string[]>): string | undefined {
  return Object.values(errores).flat()[0];
}

export async function api<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const metodo = (opciones.method ?? "GET").toUpperCase();
  const esSeguro = METODOS_SEGUROS.has(metodo);

  const enviar = async (token: string | null): Promise<Response> => {
    const headers = new Headers(opciones.headers);
    if (!esSeguro) {
      if (token) headers.set(HEADER_CSRF, token);
      if (opciones.body && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
    }
    return fetch(`${API_BASE_URL}/api${ruta}`, {
      ...opciones,
      method: metodo,
      headers,
      credentials: "include",
    });
  };

  let respuesta = await enviar(esSeguro ? null : await obtenerToken());

  // El token pudo quedar obsoleto porque la identidad cambió entremedio
  // (login, logout, expiración). Se pide uno nuevo y se reintenta una vez.
  if (!esSeguro && respuesta.status === 400) {
    const error = await leerError(respuesta.clone());
    if (error.type === TIPO_PROBLEMA_CSRF) {
      respuesta = await enviar(await obtenerToken(true));
    }
  }

  if (respuesta.status === 204) return undefined as T;

  if (!respuesta.ok) {
    const error = await leerError(respuesta);
    const errores = normalizarErrores(error.errors);
    throw new ErrorApi(
      respuesta.status,
      primerError(errores) ?? error.title ?? error.mensaje ?? `La API respondió ${respuesta.status}`,
      errores,
    );
  }

  return respuesta.json() as Promise<T>;
}

// ---- Cuenta ----

export type CuentaActual = {
  usuarioID: string;
  email: string;
  nombre: string;
  rolGlobal: string;
  esCurador: boolean;
  esAdministrador: boolean;
};

export type MetodosLogin = {
  google: boolean;
  desarrollo: boolean;
};

/** Devuelve null si no hay sesión, en vez de tratar el 401 como error. */
export async function obtenerCuentaActual(): Promise<CuentaActual | null> {
  try {
    return await api<CuentaActual>("/cuenta/me");
  } catch (e) {
    if (e instanceof ErrorApi && e.status === 401) return null;
    throw e;
  }
}

export const obtenerMetodosLogin = () => api<MetodosLogin>("/cuenta/methods");

/** Tras cambiar de identidad hay que renovar el token: el viejo ya no vale. */
async function renovarTokenTrasCambioDeIdentidad() {
  await obtenerToken(true);
}

export async function cerrarSesion(): Promise<void> {
  await api<void>("/cuenta/logout", { method: "POST" });
  await renovarTokenTrasCambioDeIdentidad();
}

export async function loginDesarrollo(email: string) {
  const resultado = await api<{ usuarioID: string; email: string; rol: string }>(
    "/cuenta/login-dev",
    { method: "POST", body: JSON.stringify({ email }) },
  );
  await renovarTokenTrasCambioDeIdentidad();
  return resultado;
}

// ---- Catálogos ----

export type Raza = { razaID: number; nombre: string };
export type Especie = { especieID: number; nombre: string; razas: Raza[] };
export type Color = { colorID: number; nombre: string };
export type TipoVisita = { tipoVisitaID: number; nombre: string };
export type Catalogos = {
  especies: Especie[];
  colores: Color[];
  tiposVisita: TipoVisita[];
};

export const obtenerCatalogos = () => api<Catalogos>("/catalogos/list");

// ---- Mascotas ----

export type EstadoRecordatorio = "Vencido" | "PorVencer" | "AlDia";

export type Recordatorio = {
  recordatorioID: number;
  titulo: string;
  claveSerie: string;
  fechaProgramada: string;
  estado: EstadoRecordatorio;
};

export type MascotaLista = {
  mascotaID: string;
  nombre: string;
  especie: string;
  raza: string | null;
  color: string | null;
  sexo: string;
  estado: string;
  edad: string | null;
  pesoKg: number | null;
  pesoFecha: string | null;
  rol: string;
  puedeEditar: boolean;
  recordatorios: Recordatorio[];
  fotoPerfilUrl?: string | null;
};

export type MascotaDetalle = {
  mascotaID: string;
  nombre: string;
  especieID: number;
  especie: string;
  razaID: number | null;
  raza: string | null;
  colorID: number | null;
  color: string | null;
  sexo: number;
  estado: number;
  fechaNacimiento: string | null;
  edad: string | null;
  lugarNacimiento: string | null;
  numeroChip: string | null;
  esterilizada: boolean | null;
  fechaEsterilizacion: string | null;
  fechaFallecimiento: string | null;
  alergias: string | null;
  observaciones: string | null;
  pesoKg: number | null;
  pesoFecha: string | null;
  rol: string;
  puedeEditar: boolean;
  esDueno: boolean;
  fotoPerfilUrl?: string | null;
};

export type GuardarMascota = {
  nombre: string;
  especieID: number;
  razaID: number | null;
  colorID: number | null;
  sexo: number;
  estado?: number;
  fechaNacimiento: string | null;
  lugarNacimiento: string | null;
  numeroChip: string | null;
  esterilizada: boolean | null;
  fechaEsterilizacion: string | null;
  fechaFallecimiento: string | null;
  alergias: string | null;
  observaciones: string | null;
  pesoKg: number | null;
};

// El recurso va en español y la acción en inglés, igual que en la API.
export const listarMascotas = () => api<MascotaLista[]>("/mascotas/list");

export const obtenerMascota = (mascotaID: string) =>
  api<MascotaDetalle>(`/mascotas/get/${mascotaID}`);

export const crearMascota = (datos: GuardarMascota) =>
  api<{ mascotaID: string }>("/mascotas/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarMascota = (mascotaID: string, datos: GuardarMascota) =>
  api<void>(`/mascotas/update/${mascotaID}`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });

export const borrarMascota = (mascotaID: string) =>
  api<void>(`/mascotas/delete/${mascotaID}`, { method: "DELETE" });

// ---- Mediciones / Serie Temporal ----

export const TipoMedicion = {
  Peso: 1,
  Temperatura: 2,
  FrecuenciaCardiaca: 3,
  CondicionCorporal: 4,
} as const;
export type TipoMedicion = (typeof TipoMedicion)[keyof typeof TipoMedicion];

export type Medicion = {
  medicionID: number;
  mascotaID: string;
  visitaID: string | null;
  tipo: TipoMedicion;
  valor: number;
  unidad: string;
  fecha: string;
  creadoUtc: string;
  esDeVisita: boolean;
};

export type GuardarMedicion = {
  mascotaID: string;
  tipo?: TipoMedicion;
  valor: number;
  fecha: string;
  unidad?: string;
};

export const listarMediciones = (mascotaID: string) =>
  api<Medicion[]>(`/mediciones/list/${mascotaID}`);

export const crearMedicion = (datos: GuardarMedicion) =>
  api<{ medicionID: number }>("/mediciones/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const borrarMedicion = (medicionID: number) =>
  api<void>(`/mediciones/delete/${medicionID}`, { method: "DELETE" });

// ---- Enums de dominio ----

export const TipoItem = {
  Vacuna: 1,
  AntiparasitarioInterno: 2,
  AntiparasitarioExterno: 3,
  Medicamento: 4,
  Procedimiento: 5,
  Examen: 6,
  Observacion: 7,
} as const;
export type TipoItem = (typeof TipoItem)[keyof typeof TipoItem];

export const TipoProducto = {
  Vacuna: 1,
  AntiparasitarioInterno: 2,
  AntiparasitarioExterno: 3,
  Medicamento: 4,
} as const;
export type TipoProducto = (typeof TipoProducto)[keyof typeof TipoProducto];

export const UnidadPeriodo = {
  Dias: 1,
  Meses: 2,
  Anios: 3,
} as const;
export type UnidadPeriodo = (typeof UnidadPeriodo)[keyof typeof UnidadPeriodo];

export const EstadoRecordatorioEnum = {
  Pendiente: 1,
  Completado: 2,
  Omitido: 3,
  Cancelado: 4,
} as const;
export type EstadoRecordatorioEnum =
  (typeof EstadoRecordatorioEnum)[keyof typeof EstadoRecordatorioEnum];

// ---- Productos ----

export type ProductoDto = {
  productoID: number;
  tipo: TipoProducto;
  nombre: string;
  laboratorio: string | null;
  especieID: number | null;
  claveSerie: string | null;
  periodicidadCantidad: number | null;
  periodicidadUnidad: UnidadPeriodo | null;
  esCurado: boolean;
};

export type GuardarProductoRequest = {
  tipo: TipoProducto;
  nombre: string;
  laboratorio?: string | null;
  especieID?: number | null;
  claveSerie?: string | null;
  periodicidadCantidad?: number | null;
  periodicidadUnidad?: UnidadPeriodo | null;
};

export type BuscarProductosFiltro = {
  q?: string;
  tipo?: TipoProducto;
  especieID?: number;
};

export function buscarProductos(filtro: BuscarProductosFiltro = {}) {
  const params = new URLSearchParams();
  if (filtro.q) params.set("q", filtro.q);
  if (filtro.tipo !== undefined) params.set("tipo", String(filtro.tipo));
  if (filtro.especieID !== undefined) params.set("especieID", String(filtro.especieID));
  const query = params.toString();
  return api<ProductoDto[]>(`/productos/search${query ? `?${query}` : ""}`);
}

export const obtenerProducto = (productoID: number) =>
  api<ProductoDto>(`/productos/get/${productoID}`);

export const crearProducto = (datos: GuardarProductoRequest) =>
  api<ProductoDto>("/productos/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

// ---- Visitas ----

export type VisitaItemDto = {
  visitaItemID: number;
  tipo: TipoItem;
  productoID: number | null;
  productoNombre: string | null;
  nombreLibre: string | null;
  descripcion: string;
  dosis: string | null;
  via: string | null;
  lote: string | null;
  numeroDosisEnSerie: number | null;
  orden: number;
  notas: string | null;
};

export type VisitaResumenDto = {
  visitaID: string;
  fecha: string;
  tipoVisitaID: number;
  tipoVisitaNombre: string;
  clinicaID: number | null;
  clinicaNombre: string | null;
  profesionalUsuarioID: string | null;
  profesionalNombre: string | null;
  motivo: string | null;
  diagnostico: string | null;
  notas: string | null;
  pesoKg: number | null;
  items: VisitaItemDto[];
  cantidadAdjuntos: number;
};

export type VisitaDetalleDto = {
  visitaID: string;
  mascotaID: string;
  fecha: string;
  tipoVisitaID: number;
  tipoVisitaNombre: string;
  clinicaID: number | null;
  clinicaNombre: string | null;
  profesionalUsuarioID: string | null;
  profesionalNombre: string | null;
  motivo: string | null;
  diagnostico: string | null;
  notas: string | null;
  pesoKg: number | null;
  items: VisitaItemDto[];
  cantidadAdjuntos: number;
  adjuntos?: Adjunto[] | null;
};

export type GuardarVisitaItemRequest = {
  tipo: TipoItem;
  productoID: number | null;
  nombreLibre: string | null;
  dosis: string | null;
  via: string | null;
  lote: string | null;
  numeroDosisEnSerie: number | null;
  notas: string | null;
};

export type GuardarVisitaRequest = {
  mascotaID: string;
  fecha: string;
  tipoVisitaID: number;
  clinicaID: number | null;
  profesionalUsuarioID: string | null;
  motivo: string | null;
  diagnostico: string | null;
  notas: string | null;
  pesoKg: number | null;
  items: GuardarVisitaItemRequest[];
};

export const listarVisitas = (mascotaID: string) =>
  api<VisitaResumenDto[]>(`/visitas/list/${mascotaID}`);

export const obtenerVisita = (visitaID: string) =>
  api<VisitaDetalleDto>(`/visitas/get/${visitaID}`);

export const crearVisita = (datos: GuardarVisitaRequest) =>
  api<{ visitaID: string }>("/visitas/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarVisita = (visitaID: string, datos: GuardarVisitaRequest) =>
  api<void>(`/visitas/update/${visitaID}`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });

export const borrarVisita = (visitaID: string) =>
  api<void>(`/visitas/delete/${visitaID}`, { method: "DELETE" });

// ---- Recetas Digitales ----

export type RecetaItemDto = {
  recetaItemID: number;
  recetaID: string;
  productoID: number | null;
  medicamentoNombre: string;
  presentacion: string | null;
  dosis: string;
  frecuencia: string;
  duracion: string | null;
  instrucciones: string | null;
  orden: number;
};

export type RecetaResumenDto = {
  recetaID: string;
  mascotaID: string;
  fechaEmision: string;
  validezDias: number;
  diagnostico: string | null;
  cantidadItems: number;
  profesionalUsuarioID: string | null;
  profesionalNombre: string | null;
  clinicaID: number | null;
  clinicaNombre: string | null;
  creadoUtc: string;
};

export type RecetaDetalleDto = {
  recetaID: string;
  mascotaID: string;
  mascotaNombre: string;
  especieNombre: string;
  razaNombre: string | null;
  sexoNombre: string | null;
  edadFormateada: string | null;
  ultimoPesoKg: number | null;
  numeroChip: string | null;
  visitaID: string | null;
  fechaEmision: string;
  validezDias: number;
  diagnostico: string | null;
  indicacionesGenerales: string | null;
  profesionalUsuarioID: string | null;
  profesionalNombre: string | null;
  profesionalTitulo: string | null;
  profesionalNumeroRegistro: string | null;
  clinicaID: number | null;
  clinicaNombre: string | null;
  clinicaDireccion: string | null;
  clinicaTelefono: string | null;
  tutorNombre: string | null;
  tutorEmail: string | null;
  tutorTelefono: string | null;
  items: RecetaItemDto[];
  creadoUtc: string;
};

export type GuardarRecetaItemRequest = {
  productoID?: number | null;
  medicamentoNombre?: string | null;
  presentacion?: string | null;
  dosis: string;
  frecuencia: string;
  duracion?: string | null;
  instrucciones?: string | null;
  orden?: number;
};

export type GuardarRecetaRequest = {
  mascotaID: string;
  fechaEmision: string;
  validezDias: number;
  visitaID?: string | null;
  clinicaID?: number | null;
  diagnostico?: string | null;
  indicacionesGenerales?: string | null;
  items: GuardarRecetaItemRequest[];
};

export const listarRecetas = (mascotaID: string) =>
  api<RecetaResumenDto[]>(`/recetas/list/${mascotaID}`);

export const obtenerReceta = (recetaID: string) =>
  api<RecetaDetalleDto>(`/recetas/get/${recetaID}`);

export const crearReceta = (datos: GuardarRecetaRequest) =>
  api<{ recetaID: string }>("/recetas/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const borrarReceta = (recetaID: string) =>
  api<void>(`/recetas/delete/${recetaID}`, { method: "DELETE" });

// ---- Recordatorios detallados ----

export type RecordatorioDetalleItemDto = {
  recordatorioID: number;
  mascotaID: string;
  origenVisitaItemID: number | null;
  claveSerie: string;
  titulo: string;
  fechaProgramada: string;
  estado: EstadoRecordatorioEnum;
  estadoSemaforo: "Vencido" | "PorVencer" | "AlDia" | "Completado" | "Cancelado";
  completadoPorVisitaItemID: number | null;
  notificadoUtc: string | null;
};

export const listarRecordatorios = (mascotaID: string) =>
  api<RecordatorioDetalleItemDto[]>(`/recordatorios/list/${mascotaID}`);

export const completarRecordatorio = (recordatorioID: number) =>
  api<void>(`/recordatorios/completar/${recordatorioID}`, { method: "POST" });

export const posponerRecordatorio = (recordatorioID: number, nuevaFecha: string) =>
  api<void>(`/recordatorios/posponer/${recordatorioID}`, {
    method: "POST",
    body: JSON.stringify({ nuevaFecha }),
  });

export const cancelarRecordatorio = (recordatorioID: number) =>
  api<void>(`/recordatorios/cancelar/${recordatorioID}`, { method: "POST" });

// ---- Módulo de datos (Curador / Back-office) ----

export type ProductoCuradorDto = {
  productoID: number;
  tipo: TipoProducto;
  nombre: string;
  nombreNormalizado: string;
  laboratorio: string | null;
  especieID: number | null;
  especieNombre: string | null;
  claveSerie: string | null;
  periodicidadCantidad: number | null;
  periodicidadUnidad: UnidadPeriodo | null;
  esCurado: boolean;
  creadoUtc: string;
  creadoPorUsuarioID: string | null;
  creadoPorNombre: string | null;
  cantidadUsos: number;
};

export type GuardarProductoCuradorRequest = {
  tipo: TipoProducto;
  nombre: string;
  laboratorio?: string | null;
  especieID?: number | null;
  claveSerie?: string | null;
  periodicidadCantidad?: number | null;
  periodicidadUnidad?: UnidadPeriodo | null;
  esCurado: boolean;
};

export type FusionarProductoRequest = {
  origenProductoID: number;
  destinoProductoID: number;
};

export type FusionResultadoDto = {
  origenProductoID: number;
  destinoProductoID: number;
  itemsReasignados: number;
};

export type EspecieCuradorDto = {
  especieID: number;
  nombre: string;
  orden: number;
  activo: boolean;
  cantidadRazas: number;
  cantidadMascotas: number;
};

export type GuardarEspecieRequest = {
  nombre: string;
  orden: number;
  activo: boolean;
};

export type RazaCuradorDto = {
  razaID: number;
  especieID: number;
  especieNombre: string;
  nombre: string;
  orden: number;
  activo: boolean;
  cantidadMascotas: number;
};

export type GuardarRazaRequest = {
  especieID: number;
  nombre: string;
  orden: number;
  activo: boolean;
};

export type ColorCuradorDto = {
  colorID: number;
  nombre: string;
  orden: number;
  activo: boolean;
  cantidadMascotas: number;
};

export type GuardarColorRequest = {
  nombre: string;
  orden: number;
  activo: boolean;
};

export type TipoVisitaCuradorDto = {
  tipoVisitaID: number;
  nombre: string;
  orden: number;
  activo: boolean;
  cantidadVisitas: number;
};

export type GuardarTipoVisitaRequest = {
  nombre: string;
  orden: number;
  activo: boolean;
};

export type FiltroProductosCurador = {
  tipo?: TipoProducto;
  especieID?: number;
  soloAportes?: boolean;
  q?: string;
  claveSerie?: string;
};

export function listarProductosCurador(filtro: FiltroProductosCurador = {}) {
  const params = new URLSearchParams();
  if (filtro.tipo !== undefined) params.set("tipo", String(filtro.tipo));
  if (filtro.especieID !== undefined) params.set("especieID", String(filtro.especieID));
  if (filtro.soloAportes !== undefined) params.set("soloAportes", String(filtro.soloAportes));
  if (filtro.q) params.set("q", filtro.q);
  if (filtro.claveSerie) params.set("claveSerie", filtro.claveSerie);
  const q = params.toString();
  return api<ProductoCuradorDto[]>(`/datos/productos/list${q ? `?${q}` : ""}`);
}

export const obtenerProductoCurador = (productoID: number) =>
  api<ProductoCuradorDto>(`/datos/productos/get/${productoID}`);

export const crearProductoCurador = (datos: GuardarProductoCuradorRequest) =>
  api<{ productoID: number }>("/datos/productos/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarProductoCurador = (
  productoID: number,
  datos: GuardarProductoCuradorRequest,
) =>
  api<void>(`/datos/productos/update/${productoID}`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });

export const promoverProducto = (productoID: number) =>
  api<void>(`/datos/productos/promover/${productoID}`, { method: "POST" });

export const fusionarProducto = (datos: FusionarProductoRequest) =>
  api<FusionResultadoDto>("/datos/productos/fusionar", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const borrarProductoCurador = (productoID: number) =>
  api<void>(`/datos/productos/delete/${productoID}`, { method: "DELETE" });

// Especies
export const listarEspeciesCurador = () =>
  api<EspecieCuradorDto[]>("/datos/especies/list");

export const crearEspecieCurador = (datos: GuardarEspecieRequest) =>
  api<{ especieID: number }>("/datos/especies/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarEspecieCurador = (especieID: number, datos: GuardarEspecieRequest) =>
  api<void>(`/datos/especies/update/${especieID}`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });

export const borrarEspecieCurador = (especieID: number) =>
  api<void>(`/datos/especies/delete/${especieID}`, { method: "DELETE" });

// Razas
export const listarRazasCurador = (especieID?: number) =>
  api<RazaCuradorDto[]>(
    `/datos/razas/list${especieID !== undefined ? `?especieID=${especieID}` : ""}`,
  );

export const crearRazaCurador = (datos: GuardarRazaRequest) =>
  api<{ razaID: number }>("/datos/razas/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarRazaCurador = (razaID: number, datos: GuardarRazaRequest) =>
  api<void>(`/datos/razas/update/${razaID}`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });

export const borrarRazaCurador = (razaID: number) =>
  api<void>(`/datos/razas/delete/${razaID}`, { method: "DELETE" });

// Colores
export const listarColoresCurador = () =>
  api<ColorCuradorDto[]>("/datos/colores/list");

export const crearColorCurador = (datos: GuardarColorRequest) =>
  api<{ colorID: number }>("/datos/colores/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarColorCurador = (colorID: number, datos: GuardarColorRequest) =>
  api<void>(`/datos/colores/update/${colorID}`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });

export const borrarColorCurador = (colorID: number) =>
  api<void>(`/datos/colores/delete/${colorID}`, { method: "DELETE" });

// Tipos de Visita
export const listarTiposVisitaCurador = () =>
  api<TipoVisitaCuradorDto[]>("/datos/tipos-visita/list");

export const crearTipoVisitaCurador = (datos: GuardarTipoVisitaRequest) =>
  api<{ tipoVisitaID: number }>("/datos/tipos-visita/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarTipoVisitaCurador = (
  tipoVisitaID: number,
  datos: GuardarTipoVisitaRequest,
) =>
  api<void>(`/datos/tipos-visita/update/${tipoVisitaID}`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });

export const borrarTipoVisitaCurador = (tipoVisitaID: number) =>
  api<void>(`/datos/tipos-visita/delete/${tipoVisitaID}`, { method: "DELETE" });

// ---- Adjuntos ----

export const PropositoAdjunto = {
  FotoPerfil: 1,
  Carnet: 2,
  Receta: 3,
  Examen: 4,
  Otro: 99,
} as const;
export type PropositoAdjunto = (typeof PropositoAdjunto)[keyof typeof PropositoAdjunto];

export type Adjunto = {
  adjuntoID: string;
  mascotaID: string;
  visitaID: string | null;
  visitaItemID: number | null;
  proposito: PropositoAdjunto;
  contentType: string;
  bytes: number;
  width: number;
  height: number;
  creadoUtc: string;
  url: string;
  thumbnailUrl?: string | null;
};

export type PrepararSubidaRequest = {
  mascotaID: string;
  visitaID?: string | null;
  visitaItemID?: number | null;
  proposito: PropositoAdjunto;
  nombreArchivo: string;
  contentType: string;
  bytes: number;
};

export type PrepararSubidaResponse = {
  adjuntoID: string;
  storageKey: string;
  uploadUrl: string;
  expiraUtc: string;
  thumbnailKey?: string | null;
  thumbnailUploadUrl?: string | null;
};

export type ConfirmarSubidaRequest = {
  adjuntoID: string;
  mascotaID: string;
  visitaID?: string | null;
  visitaItemID?: number | null;
  proposito: PropositoAdjunto;
  storageKey: string;
  thumbnailKey?: string | null;
  contentType: string;
  bytes: number;
  width?: number | null;
  height?: number | null;
};

export const prepararSubidaAdjunto = (datos: PrepararSubidaRequest) =>
  api<PrepararSubidaResponse>("/adjuntos/preparar-subida", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const confirmarSubidaAdjunto = (datos: ConfirmarSubidaRequest) =>
  api<Adjunto>("/adjuntos/confirmar", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const listarAdjuntos = (
  mascotaID: string,
  params?: { visitaID?: string; proposito?: PropositoAdjunto },
) => {
  const query = new URLSearchParams();
  if (params?.visitaID) query.set("visitaID", params.visitaID);
  if (params?.proposito) query.set("proposito", String(params.proposito));
  const qs = query.toString();
  return api<Adjunto[]>(`/adjuntos/list/${mascotaID}${qs ? `?${qs}` : ""}`);
};

export const obtenerAdjunto = (adjuntoID: string) =>
  api<Adjunto>(`/adjuntos/get/${adjuntoID}`);

export const borrarAdjunto = (adjuntoID: string) =>
  api<void>(`/adjuntos/delete/${adjuntoID}`, { method: "DELETE" });

/**
 * Orquesta la subida directa de un archivo a Azure Blob Storage:
 * 1. Pide la URL SAS de subida a la API.
 * 2. Realiza el PUT directo al Blob Storage (sin saturar la API).
 * 3. Confirma el registro del adjunto con sus dimensiones y metadata.
 */
/**
 * Genera una miniatura WebP en el navegador (client-side) escalada a un tama?o m?ximo (por defecto 360px).
 * Devuelve null si no es imagen o si el navegador no puede renderizarla.
 */
export async function generarMiniaturaCliente(archivo: File, maxTamano: number = 360): Promise<Blob | null> {
  if (!archivo.type.startsWith("image/")) return null;

  try {
    const url = URL.createObjectURL(archivo);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const elemento = new Image();
      elemento.onload = () => resolve(elemento);
      elemento.onerror = reject;
      elemento.src = url;
    });
    URL.revokeObjectURL(url);

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (w <= 0 || h <= 0) return null;

    let targetW = w;
    let targetH = h;
    if (w > maxTamano || h > maxTamano) {
      if (w >= h) {
        targetW = maxTamano;
        targetH = Math.round((h * maxTamano) / w);
      } else {
        targetH = maxTamano;
        targetW = Math.round((w * maxTamano) / h);
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, targetW, targetH);

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/webp", 0.82);
    });
  } catch {
    return null;
  }
}

export async function subirAdjuntoDirecto(
  archivo: File,
  datos: {
    mascotaID: string;
    visitaID?: string | null;
    visitaItemID?: number | null;
    proposito: PropositoAdjunto;
  },
): Promise<Adjunto> {
  // 1. Preparar subida (la API devuelve uploadUrl para el original y opcionalmente thumbnailUploadUrl si es imagen)
  const preparacion = await prepararSubidaAdjunto({
    mascotaID: datos.mascotaID,
    visitaID: datos.visitaID,
    visitaItemID: datos.visitaItemID,
    proposito: datos.proposito,
    nombreArchivo: archivo.name,
    contentType: archivo.type || "application/octet-stream",
    bytes: archivo.size,
  });

  // 2. Si es imagen y la API asign? URL de miniatura, generar miniatura en el cliente
  let thumbnailBlob: Blob | null = null;
  if (preparacion.thumbnailUploadUrl && archivo.type.startsWith("image/")) {
    thumbnailBlob = await generarMiniaturaCliente(archivo, 360);
  }

  // 3. Subir original y miniatura de forma concurrente directo a Azure Blob Storage
  const subidaOriginal = fetch(preparacion.uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": archivo.type || "application/octet-stream",
    },
    body: archivo,
  });

  const subidaThumbnail =
    preparacion.thumbnailUploadUrl && thumbnailBlob
      ? fetch(preparacion.thumbnailUploadUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": "image/webp",
          },
          body: thumbnailBlob,
        })
      : Promise.resolve(null);

  const [resSubida, resThumb] = await Promise.all([subidaOriginal, subidaThumbnail]);

  if (!resSubida.ok) {
    throw new Error(
      `Error al subir el archivo a Blob Storage: ${resSubida.status} ${resSubida.statusText}`,
    );
  }

  // 4. Extraer dimensiones si es imagen
  let width: number | null = null;
  let height: number | null = null;
  if (archivo.type.startsWith("image/")) {
    try {
      const dimensiones = await new Promise<{ w: number; h: number }>((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(archivo);
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ w: img.naturalWidth, h: img.naturalHeight });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject();
        };
        img.src = url;
      });
      width = dimensiones.w;
      height = dimensiones.h;
    } catch {
      // Ignorar si no se pudieron leer las dimensiones
    }
  }

  // 5. Confirmar registro en la API incluyendo thumbnailKey si se subi? exitosamente
  return await confirmarSubidaAdjunto({
    adjuntoID: preparacion.adjuntoID,
    mascotaID: datos.mascotaID,
    visitaID: datos.visitaID,
    visitaItemID: datos.visitaItemID,
    proposito: datos.proposito,
    storageKey: preparacion.storageKey,
    thumbnailKey: resThumb && resThumb.ok ? preparacion.thumbnailKey : null,
    contentType: archivo.type || "application/octet-stream",
    bytes: archivo.size,
    width,
    height,
  });
}

// ---- Accesos e Invitaciones ----

export enum RolAcceso {
  Dueno = 1,
  CoDueno = 2,
  Veterinario = 3,
  Cuidador = 4,
  Lector = 5,
}

export type CrearInvitacionRequest = {
  rolOtorgado: RolAcceso;
  puedeEditar: boolean;
  duracionAccesoDias: number;
  duracionInvitacionHoras: number;
  usosMaximos: number;
};

export type InvitacionDetalle = {
  invitacionID: number;
  mascotaID: string;
  token: string;
  urlInvitacion: string;
  rolOtorgado: RolAcceso;
  puedeEditar: boolean;
  duracionAccesoDias: number;
  venceUtc: string;
  usosMaximos: number;
  usosActuales: number;
  esValida: boolean;
  creadoUtc: string;
};

export type InvitacionPublica = {
  token: string;
  mascotaID: string;
  nombreMascota: string;
  especie: string;
  fotoPerfilUrl: string | null;
  rolOtorgado: RolAcceso;
  puedeEditar: boolean;
  duracionAccesoDias: number;
  venceUtc: string;
  esValida: boolean;
  motivoInvalidez: string | null;
};

export type CanjearInvitacionResponse = {
  mascotaID: string;
  nombreMascota: string;
  rol: RolAcceso;
  puedeEditar: boolean;
  hastaUtc: string | null;
};

export type MascotaAccesoItem = {
  mascotaAccesoID: number;
  mascotaID: string;
  usuarioID: string;
  nombreCompleto: string;
  email: string;
  rol: RolAcceso;
  puedeEditar: boolean;
  desdeUtc: string;
  hastaUtc: string | null;
  revocado: boolean;
  esVigente: boolean;
  esDueno: boolean;
};

export const crearInvitacion = (mascotaID: string, req: CrearInvitacionRequest) =>
  api<InvitacionDetalle>(`/accesos/${mascotaID}/invitaciones`, {
    method: "POST",
    body: JSON.stringify(req),
  });

export const listarInvitaciones = (mascotaID: string) =>
  api<InvitacionDetalle[]>(`/accesos/${mascotaID}/invitaciones`);

export const cancelarInvitacion = (mascotaID: string, invitacionID: number) =>
  api<void>(`/accesos/${mascotaID}/invitaciones/${invitacionID}`, { method: "DELETE" });

export const listarAccesosMascota = (mascotaID: string) =>
  api<MascotaAccesoItem[]>(`/accesos/${mascotaID}/list`);

export const revocarAccesoMascota = (mascotaID: string, mascotaAccesoID: number) =>
  api<void>(`/accesos/${mascotaID}/revocar/${mascotaAccesoID}`, { method: "POST" });

export const obtenerInvitacionPublica = (token: string) =>
  api<InvitacionPublica>(`/invitaciones/${token}`);

export const canjearInvitacion = (token: string) =>
  api<CanjearInvitacionResponse>(`/invitaciones/${token}/canjear`, { method: "POST" });

// ==========================================
// ---- Módulo de Administración (Admin) ----
// ==========================================

export const RolGlobal = {
  Persona: 1,
  Curador: 2,
  Administrador: 3,
} as const;
export type RolGlobal = (typeof RolGlobal)[keyof typeof RolGlobal];

export type UsuarioAdminDto = {
  usuarioID: string;
  email: string;
  nombres: string;
  apellidos: string | null;
  nombreCompleto: string;
  telefono: string | null;
  activo: boolean;
  rolGlobal: RolGlobal;
  esVeterinario: boolean;
  veterinarioVerificado: boolean;
  cantidadMascotas: number;
  creadoUtc: string;
  modificadoUtc: string;
};

export type UsuarioMascotaAccesoDto = {
  mascotaID: string;
  nombreMascota: string;
  especie: string;
  rol: RolAcceso;
  puedeEditar: boolean;
  desdeUtc: string;
  hastaUtc: string | null;
  revocado: boolean;
};

export type UsuarioDetalleAdminDto = {
  usuarioID: string;
  email: string;
  nombres: string;
  apellidos: string | null;
  nombreCompleto: string;
  telefono: string | null;
  activo: boolean;
  rolGlobal: RolGlobal;
  perfilProfesional: PerfilProfesionalAdminDto | null;
  mascotas: UsuarioMascotaAccesoDto[];
  creadoUtc: string;
  modificadoUtc: string;
};

export type PerfilProfesionalAdminDto = {
  usuarioID: string;
  email: string;
  nombreCompleto: string;
  numeroRegistro: string | null;
  titulo: string | null;
  clinicaID: number | null;
  clinicaNombre: string | null;
  verificado: boolean;
};

export type GuardarPerfilProfesionalRequest = {
  usuarioID: string;
  numeroRegistro?: string | null;
  titulo?: string | null;
  clinicaID?: number | null;
  verificado: boolean;
};

export type ClinicaAdminDto = {
  clinicaID: number;
  nombre: string;
  direccion: string | null;
  comuna: string | null;
  telefono: string | null;
  cantidadProfesionales: number;
  creadoUtc: string;
  modificadoUtc: string;
};

export type GuardarClinicaRequest = {
  nombre: string;
  direccion?: string | null;
  comuna?: string | null;
  telefono?: string | null;
};

export type ConfiguracionDto = {
  clave: string;
  valor: string;
  descripcion: string | null;
  modificadoUtc: string;
};

export type DiagnosticoResumenDto = {
  totalUsuarios: number;
  totalUsuariosActivos: number;
  totalCuradores: number;
  totalAdministradores: number;
  totalMascotas: number;
  totalMascotasActivas: number;
  totalVisitas: number;
  totalRecordatoriosPendientes: number;
  totalRecordatoriosVencidos: number;
  totalAdjuntos: number;
  estadoBaseDatos: string;
  entorno: string;
  servidorHoraUtc: string;
};

// Endpoints Admin

export const listarUsuariosAdmin = (filtro?: {
  q?: string;
  rolGlobal?: RolGlobal;
  activo?: boolean;
}) => {
  const p = new URLSearchParams();
  if (filtro?.q) p.set("q", filtro.q);
  if (filtro?.rolGlobal != null) p.set("rolGlobal", String(filtro.rolGlobal));
  if (filtro?.activo != null) p.set("activo", String(filtro.activo));
  const query = p.toString();
  return api<UsuarioAdminDto[]>(`/admin/usuarios/list${query ? `?${query}` : ""}`);
};

export const obtenerUsuarioAdmin = (usuarioID: string) =>
  api<UsuarioDetalleAdminDto>(`/admin/usuarios/get/${usuarioID}`);

export const cambiarRolUsuarioAdmin = (usuarioID: string, rolGlobal: RolGlobal) =>
  api<{ mensaje: string; usuarioID: string; rolGlobal: RolGlobal }>(
    `/admin/usuarios/cambiar-rol/${usuarioID}`,
    {
      method: "PUT",
      body: JSON.stringify({ rolGlobal }),
    },
  );

export const cambiarEstadoUsuarioAdmin = (usuarioID: string, activo: boolean) =>
  api<{ mensaje: string; usuarioID: string; activo: boolean }>(
    `/admin/usuarios/cambiar-estado/${usuarioID}`,
    {
      method: "PUT",
      body: JSON.stringify({ activo }),
    },
  );

export const listarProfesionalesAdmin = (filtro?: {
  q?: string;
  verificado?: boolean;
  clinicaID?: number;
}) => {
  const p = new URLSearchParams();
  if (filtro?.q) p.set("q", filtro.q);
  if (filtro?.verificado != null) p.set("verificado", String(filtro.verificado));
  if (filtro?.clinicaID != null) p.set("clinicaID", String(filtro.clinicaID));
  const query = p.toString();
  return api<PerfilProfesionalAdminDto[]>(`/admin/profesionales/list${query ? `?${query}` : ""}`);
};

export const verificarProfesionalAdmin = (usuarioID: string, verificado: boolean) =>
  api<{ mensaje: string; usuarioID: string; verificado: boolean }>(
    `/admin/profesionales/verificar/${usuarioID}`,
    {
      method: "PUT",
      body: JSON.stringify({ verificado }),
    },
  );

export const guardarProfesionalAdmin = (req: GuardarPerfilProfesionalRequest) =>
  api<{ mensaje: string; usuarioID: string; verificado: boolean }>(
    "/admin/profesionales/guardar",
    {
      method: "POST",
      body: JSON.stringify(req),
    },
  );

export const listarClinicasAdmin = (filtro?: { q?: string }) => {
  const p = new URLSearchParams();
  if (filtro?.q) p.set("q", filtro.q);
  const query = p.toString();
  return api<ClinicaAdminDto[]>(`/admin/clinicas/list${query ? `?${query}` : ""}`);
};

export const crearClinicaAdmin = (datos: GuardarClinicaRequest) =>
  api<{ mensaje: string; clinicaID: number; nombre: string }>("/admin/clinicas/create", {
    method: "POST",
    body: JSON.stringify(datos),
  });

export const actualizarClinicaAdmin = (clinicaID: number, datos: GuardarClinicaRequest) =>
  api<{ mensaje: string; clinicaID: number; nombre: string }>(
    `/admin/clinicas/update/${clinicaID}`,
    {
      method: "PUT",
      body: JSON.stringify(datos),
    },
  );

export const borrarClinicaAdmin = (clinicaID: number) =>
  api<{ mensaje: string }>(`/admin/clinicas/delete/${clinicaID}`, { method: "DELETE" });

export const obtenerConfiguracionesAdmin = () =>
  api<ConfiguracionDto[]>("/admin/configuracion/list");

export const actualizarConfiguracionesAdmin = (valores: Record<string, string>) =>
  api<{ mensaje: string; totalActualizados: number }>("/admin/configuracion/update", {
    method: "PUT",
    body: JSON.stringify({ valores }),
  });

export const obtenerDiagnosticoAdmin = () =>
  api<DiagnosticoResumenDto>("/admin/diagnostico/resumen");

export interface NotificacionesResumenDto {
  totalPendientes: number;
  totalVencidos: number;
  totalPorVencer: number;
  totalNotificados: number;
  diasAvisoVencimiento: number;
  emailHabilitado: boolean;
  proveedorEmail: string;
  ultimaEjecucionUtc: string | null;
}

export interface DispararJobResponseDto {
  mensaje: string;
  trabajoId: string;
  disparadoUtc: string;
}

export const obtenerNotificacionesAdmin = () =>
  api<NotificacionesResumenDto>("/admin/notificaciones/resumen");

export const dispararJobNotificacionesAdmin = () =>
  api<DispararJobResponseDto>("/admin/notificaciones/disparar-job", {
    method: "POST",
    body: JSON.stringify({}),
  });

// ==========================================
// ---- Perfil y Datos del Tutor ------------
// ==========================================

export type PerfilTutor = {
  usuarioID: string;
  nombres: string;
  apellidos: string | null;
  nombreCompleto: string;
  email: string;
  rut: string | null;
  telefono: string | null;
  telefonoEmergencia: string | null;
  direccion: string | null;
  comuna: string | null;
  notasContacto: string | null;
};

export type GuardarPerfilTutorRequest = {
  nombres: string;
  apellidos: string | null;
  rut: string | null;
  telefono: string | null;
  telefonoEmergencia: string | null;
  direccion: string | null;
  comuna: string | null;
  notasContacto: string | null;
};

export function obtenerPerfilTutorLocal(
  usuarioID: string,
  defaultNombre = "",
  defaultEmail = "",
): PerfilTutor {
  try {
    const raw = localStorage.getItem(`fv_perfil_${usuarioID}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        usuarioID,
        nombres: parsed.nombres || defaultNombre || "Usuario",
        apellidos: parsed.apellidos || null,
        nombreCompleto: [parsed.nombres, parsed.apellidos].filter(Boolean).join(" ").trim() || defaultNombre || "Usuario",
        email: parsed.email || defaultEmail,
        rut: parsed.rut || null,
        telefono: parsed.telefono || null,
        telefonoEmergencia: parsed.telefonoEmergencia || null,
        direccion: parsed.direccion || null,
        comuna: parsed.comuna || null,
        notasContacto: parsed.notasContacto || null,
      };
    }
  } catch {}

  const partes = defaultNombre.trim().split(" ");
  const nombres = partes.length > 1 ? partes.slice(0, -1).join(" ") : defaultNombre;
  const apellidos = partes.length > 1 ? partes[partes.length - 1] : null;

  return {
    usuarioID,
    nombres: nombres || "Usuario",
    apellidos: apellidos || null,
    nombreCompleto: defaultNombre || "Usuario",
    email: defaultEmail,
    rut: null,
    telefono: null,
    telefonoEmergencia: null,
    direccion: null,
    comuna: null,
    notasContacto: null,
  };
}

export function guardarPerfilTutorLocal(
  usuarioID: string,
  req: GuardarPerfilTutorRequest,
  email: string,
): PerfilTutor {
  const nombreCompleto = [req.nombres, req.apellidos].filter(Boolean).join(" ").trim();
  const perfil: PerfilTutor = {
    usuarioID,
    nombres: req.nombres.trim(),
    apellidos: req.apellidos?.trim() || null,
    nombreCompleto: nombreCompleto || "Usuario",
    email,
    rut: req.rut?.trim() || null,
    telefono: req.telefono?.trim() || null,
    telefonoEmergencia: req.telefonoEmergencia?.trim() || null,
    direccion: req.direccion?.trim() || null,
    comuna: req.comuna?.trim() || null,
    notasContacto: req.notasContacto?.trim() || null,
  };

  try {
    localStorage.setItem(`fv_perfil_${usuarioID}`, JSON.stringify(perfil));
  } catch {}

  return perfil;
}

// =======================================================
// ---- Fase 4: Carnet de Vacunación y Ficha Pública ----
// =======================================================

export type CarnetMascotaDto = {
  mascotaID: string;
  nombre: string;
  especieID: number;
  especie: string;
  razaID: number | null;
  raza: string | null;
  colorID: number | null;
  color: string | null;
  sexo: number;
  estado: number;
  fechaNacimiento: string | null;
  edad: string | null;
  numeroChip: string | null;
  esterilizada: boolean | null;
  fechaEsterilizacion: string | null;
  pesoKg: number | null;
  pesoFecha: string | null;
  alergias: string | null;
  observaciones: string | null;
  fotoPerfilUrl: string | null;
};

export type CarnetTutorDto = {
  nombreCompleto: string;
  rut: string | null;
  email: string | null;
  telefono: string | null;
  telefonoEmergencia: string | null;
  direccion: string | null;
  comuna: string | null;
  notasContacto: string | null;
};

export type VacunaRegistroDto = {
  visitaItemID: number;
  fecha: string;
  productoID: number | null;
  nombre: string;
  dosis: string | null;
  via: string | null;
  lote: string | null;
  numeroDosisEnSerie: number | null;
  clinicaNombre: string | null;
  profesionalNombre: string | null;
  notas: string | null;
};

export type DesparasitacionRegistroDto = {
  visitaItemID: number;
  fecha: string;
  tipo: TipoItem;
  tipoNombre: string;
  productoID: number | null;
  nombre: string;
  dosis: string | null;
  via: string | null;
  lote: string | null;
  clinicaNombre: string | null;
  profesionalNombre: string | null;
  notas: string | null;
};

export type RefuerzoPendienteDto = {
  recordatorioID: number;
  titulo: string;
  claveSerie: string;
  fechaProgramada: string;
  estadoSemaforo: "AlDia" | "PorVencer" | "Vencido";
  diasRestantes: number;
};

export type CarnetVacunacionDto = {
  mascota: CarnetMascotaDto;
  tutor: CarnetTutorDto;
  vacunas: VacunaRegistroDto[];
  desparasitaciones: DesparasitacionRegistroDto[];
  refuerzosPendientes: RefuerzoPendienteDto[];
  estadoGeneral: "AlDia" | "PorVencer" | "Vencido" | "SinRegistros";
  totalVacunas: number;
  totalDesparasitaciones: number;
  ultimaVacunacionFecha: string | null;
  ultimaDesparasitacionFecha: string | null;
  tokenPublico: string | null;
  compartirPublicoHabilitado: boolean;
  urlPublica: string | null;
};

export type ConfigurarCarnetPublicoRequest = {
  habilitado?: boolean;
  regenerarToken?: boolean;
};

export const obtenerCarnetVacunacion = (mascotaID: string) =>
  api<CarnetVacunacionDto>(`/mascotas/${mascotaID}/carnet`);

export const configurarCarnetPublico = (
  mascotaID: string,
  req: ConfigurarCarnetPublicoRequest,
) =>
  api<{
    tokenPublico: string;
    compartirPublicoHabilitado: boolean;
    urlPublica: string;
  }>(`/mascotas/${mascotaID}/carnet/publico`, {
    method: "POST",
    body: JSON.stringify(req),
  });

export const obtenerCarnetPublico = (token: string) =>
  api<CarnetVacunacionDto>(`/publico/carnet/${token}`);



